import type { SuggestionColor, SuggestionIcon } from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';

import {
  type AdminSuggestion,
  SUGGESTION_COLOR,
  SUGGESTION_ICON,
  apiErrorMessage,
} from './suggestion-meta';

const FIELD =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';
const LABEL = 'mb-1 block text-xs font-medium text-slate-700';

interface OrgOption {
  id: string;
  name: string;
  type: 'ADWEB' | 'CLIENT';
}

/** Création / modification d'une suggestion (rôles Adweb). */
export function SuggestionEditorModal({
  open,
  suggestion,
  categories,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** null : nouvelle suggestion (créée en brouillon). */
  suggestion: AdminSuggestion | null;
  categories: string[];
  onClose: () => void;
  onSaved: (s: AdminSuggestion) => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [example, setExample] = useState('');
  const [category, setCategory] = useState('');
  const [hours, setHours] = useState('');
  const [icon, setIcon] = useState<SuggestionIcon>('sparkles');
  const [color, setColor] = useState<SuggestionColor>('blue');
  const [targetAll, setTargetAll] = useState(true);
  const [targets, setTargets] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(suggestion?.title ?? '');
    setTagline(suggestion?.tagline ?? '');
    setDescription(suggestion?.description ?? '');
    setExample(suggestion?.example ?? '');
    setCategory(suggestion?.category ?? '');
    setHours(suggestion?.hoursSavedPerMonth ? String(suggestion.hoursSavedPerMonth) : '');
    setIcon(suggestion?.icon ?? 'sparkles');
    setColor(suggestion?.color ?? 'blue');
    setTargetAll(suggestion?.targetAll ?? true);
    setTargets(suggestion?.targets.map((t) => t.id) ?? []);
    setError(null);
  }, [open, suggestion]);

  const orgs = useQuery({
    queryKey: ['organizations', 'for-filter-typed'],
    queryFn: async () =>
      (await apiClient.get<{ data: OrgOption[] }>('/organizations?pageSize=100')).data.data,
    enabled: open,
    staleTime: 60_000,
  });
  const clientOrgs = (orgs.data ?? [])
    .filter((o) => o.type === 'CLIENT')
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  const save = useMutation({
    mutationFn: async () => {
      const parsedHours = Number(hours.replace(',', '.'));
      const body = {
        title,
        tagline,
        description,
        example: example.trim() || null,
        category: category.trim() || null,
        hoursSavedPerMonth: hours.trim() && parsedHours > 0 ? parsedHours : null,
        icon,
        color,
        targetAll,
        targetOrganizationIds: targetAll ? [] : targets,
      };
      const res = suggestion
        ? await apiClient.patch<AdminSuggestion>(`/suggestions/${suggestion.id}`, body)
        : await apiClient.post<AdminSuggestion>('/suggestions', body);
      return res.data;
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['suggestions'] });
      onSaved(saved);
    },
    onError: (err) =>
      setError(apiErrorMessage(err, 'L’enregistrement a échoué. Vérifiez les champs.')),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!targetAll && targets.length === 0) {
      setError('Choisissez au moins une organisation, ou proposez-la à toutes.');
      return;
    }
    save.mutate();
  }

  function toggleTarget(id: string) {
    setTargets((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={suggestion ? 'Modifier la suggestion' : 'Nouvelle suggestion'}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Annuler
          </Button>
          <Button form="suggestion-form" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Enregistrement…' : suggestion ? 'Enregistrer' : 'Créer le brouillon'}
          </Button>
        </>
      }
    >
      <form id="suggestion-form" onSubmit={submit} className="space-y-4">
        {!suggestion && (
          <p className="text-sm text-slate-600">
            La suggestion est créée en brouillon : seuls les comptes Adweb-Systèmes la voient
            jusqu’à sa publication.
          </p>
        )}
        <div>
          <label htmlFor="sugg-title" className={LABEL}>
            Titre <span className="text-red-600">*</span>
          </label>
          <input
            id="sugg-title"
            required
            minLength={3}
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex. Assistant qui répond aux questions sur vos documents"
            className={FIELD}
          />
        </div>
        <div>
          <label htmlFor="sugg-tagline" className={LABEL}>
            Accroche <span className="text-red-600">*</span>
          </label>
          <input
            id="sugg-tagline"
            required
            minLength={3}
            maxLength={200}
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="Une phrase : le bénéfice pour le client"
            className={FIELD}
          />
        </div>
        <div>
          <label htmlFor="sugg-description" className={LABEL}>
            Description <span className="text-red-600">*</span>
          </label>
          <textarea
            id="sugg-description"
            required
            minLength={10}
            maxLength={4000}
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ce que fait le service, ce que le client y gagne, ce qu’il faut pour démarrer…"
            className={FIELD}
          />
        </div>
        <div>
          <label htmlFor="sugg-example" className={LABEL}>
            Exemple concret
          </label>
          <textarea
            id="sugg-example"
            maxLength={2000}
            rows={2}
            value={example}
            onChange={(e) => setExample(e.target.value)}
            placeholder="Ex. « Quel est le délai de préavis du contrat Dupont ? » → réponse en 5 secondes, avec la source."
            className={FIELD}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="sugg-category" className={LABEL}>
              Catégorie
            </label>
            <input
              id="sugg-category"
              maxLength={60}
              list="sugg-categories"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Ex. Relation client"
              className={FIELD}
            />
            <datalist id="sugg-categories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor="sugg-hours" className={LABEL}>
              Gain estimé (heures / mois)
            </label>
            <input
              id="sugg-hours"
              inputMode="decimal"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="Ex. 6"
              className={FIELD}
            />
          </div>
        </div>

        <fieldset>
          <legend className={LABEL}>Icône</legend>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(SUGGESTION_ICON) as SuggestionIcon[]).map((key) => {
              const { icon: Icon, label } = SUGGESTION_ICON[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  aria-pressed={icon === key}
                  title={label}
                  aria-label={label}
                  className={clsx(
                    'flex h-9 w-9 items-center justify-center rounded-lg ring-1 transition',
                    icon === key
                      ? clsx(SUGGESTION_COLOR[color].chip, 'ring-2 ring-brand')
                      : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50',
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend className={LABEL}>Couleur</legend>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(SUGGESTION_COLOR) as SuggestionColor[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setColor(key)}
                aria-pressed={color === key}
                aria-label={SUGGESTION_COLOR[key].label}
                title={SUGGESTION_COLOR[key].label}
                className={clsx(
                  'h-8 w-8 rounded-full transition',
                  SUGGESTION_COLOR[key].bar,
                  color === key
                    ? 'ring-2 ring-brand ring-offset-2'
                    : 'opacity-80 hover:opacity-100',
                )}
              />
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className={LABEL}>Proposée à</legend>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="sugg-target"
              checked={targetAll}
              onChange={() => setTargetAll(true)}
            />
            Toutes les organisations clientes
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="sugg-target"
              checked={!targetAll}
              onChange={() => setTargetAll(false)}
            />
            Certaines organisations seulement
          </label>
          {!targetAll && (
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-3">
              {orgs.isLoading && <p className="text-xs text-slate-400">Chargement…</p>}
              {orgs.data && clientOrgs.length === 0 && (
                <p className="text-xs text-slate-500">Aucune organisation cliente.</p>
              )}
              {clientOrgs.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={targets.includes(o.id)}
                    onChange={() => toggleTarget(o.id)}
                  />
                  {o.name}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
